import { EllipsisIcon } from "assets";
import styles from "./styles.module.scss";
import { ReactNode, useState } from "react";
import * as Popover from "@radix-ui/react-popover";

export interface DropdownMenuOption {
  text: string | ReactNode;
  onClick: () => void;
  key?: string;
  /** Highlights the option (e.g. the currently-selected sort) with a light-blue background. */
  active?: boolean;
}

interface DropdownMenuProps {
  options: DropdownMenuOption[];
  ellipsisClassName?: string;
  trigger?: ReactNode;
  menu?: (close: () => void) => ReactNode;
  menuClassName?: string;
  align?: "left" | "right"; // control horizontal alignment
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  options,
  ellipsisClassName,
  trigger,
  menu,
  menuClassName,
  align = "right", // default keeps previous behavior
}) => {
  const [open, setOpen] = useState(false);

  const closeMenu = () => setOpen(false);

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <div className={styles.menu}>
        <Popover.Trigger asChild>
          <div className={styles.menu__cta}>
            {trigger || (
              <EllipsisIcon className={ellipsisClassName} role="button" />
            )}
          </div>
        </Popover.Trigger>

        <Popover.Portal>
          {/*
            Radix measures and places the content before it paints, so the menu
            no longer flashes in a corner before snapping into position. It also
            handles outside-click, Escape, scroll/resize tracking and viewport
            collision, which this component used to wire up by hand.
          */}
          <Popover.Content
            side="bottom"
            sideOffset={8}
            align={align === "right" ? "end" : "start"}
            collisionPadding={8}
            className={`${styles.menu__options} ${menuClassName ?? ""} w-fit`}
            style={{
              zIndex: 10000,
              minWidth: "var(--radix-popover-trigger-width)",
            }}
          >
            {menu
              ? menu(closeMenu)
              : options.map(({ text, onClick, key, active }, idx) => (
                  <button
                    key={key ?? idx}
                    onClick={() => {
                      onClick();
                      closeMenu();
                    }}
                    className="block w-full text-left px-4 py-2 hover:bg-gray-100"
                    style={active ? { background: "#EFF8FF" } : undefined}
                  >
                    {text}
                  </button>
                ))}
          </Popover.Content>
        </Popover.Portal>
      </div>
    </Popover.Root>
  );
};
